import React from 'react';
import {useSearchParams} from 'react-router';
import {Stack, CircularProgress, Box, Alert} from '@mui/material';
import FeedReportPreview from './FeedReportPreview';
import SourcesFileViewerContent from '../../../products/reportEditorPage/leftPanel/components/SourcesFileViewerContent';
import useDocumentContent from '../../../../components/evidenceModal/useDocumentContent';
import DocumentContent from '../../../../components/DocumentContent';

/**
 * Inline corpus document viewer for the feed's right panel.
 * Reuses the same DocumentContent component as the evidence modal but without
 * the SourcePackageBuilder store — props come directly from URL query params.
 */
const FeedCorpusPreview = ({docId}) => {
    const {isLoading, docData} = useDocumentContent(docId);

    if (isLoading) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" height="100%">
                <CircularProgress size={32}/>
            </Stack>
        );
    }

    if (!docData) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" height="100%" px={2}>
                <Alert severity="warning">Could not load document.</Alert>
            </Stack>
        );
    }

    return (
        <Box sx={{flex: 1, overflow: 'auto', height: '100%'}}>
            <DocumentContent props={docData} savedHighlights={[]} searchResultSegmentIds={[]}/>
        </Box>
    );
};

/**
 * Unified right-panel preview for the feeds page.
 *
 * Routing:
 *   - FINTEL report:  URL has :sourceSegmentId path param (no ?previewType) → <FeedReportPreview>
 *   - Corpus doc:     ?previewType=corpus&docId=N  → inline corpus view
 *   - Data item:      ?previewType=data_item&fileId=N&canonicalFileId=M  → inline data-item view
 */
const FeedPreviewPanel = () => {
    const [searchParams] = useSearchParams();
    const previewType = searchParams.get('previewType');

    if (previewType === 'corpus') {
        const docId = searchParams.get('docId');
        return <FeedCorpusPreview docId={docId}/>;
    }

    if (previewType === 'data_item') {
        const fileId = searchParams.get('fileId');
        const canonicalFileId = searchParams.get('canonicalFileId');
        const fileName = searchParams.get('fileName') || null;
        let sentenceHit = null;
        try {
            const raw = searchParams.get('sentenceHit');
            if (raw) sentenceHit = JSON.parse(raw);
        } catch {
            // ignore malformed sentenceHit
        }

        return (
            <Box sx={{flex: 1, overflow: 'auto', height: '100%'}}>
                <SourcesFileViewerContent
                    fileId={fileId}
                    canonicalFileId={canonicalFileId}
                    fileName={fileName}
                    sentenceHit={sentenceHit}
                    active
                />
            </Box>
        );
    }

    // Default: FINTEL report preview (driven by :sourceSegmentId route param)
    return <FeedReportPreview/>;
};

export default FeedPreviewPanel;
